import {Component, OnInit, AfterViewChecked, ViewChild, ChangeDetectorRef, AfterContentChecked, HostListener} from '@angular/core'
import {ActivatedRoute, RouterOutlet, Router, NavigationEnd, UrlSerializer} from '@angular/router'
import {filter, map, share, pairwise, startWith} from 'rxjs/operators'
import {AngularFirestore} from 'angularfire2/firestore'
import {AngularFireAuth} from 'angularfire2/auth'
import {Observable} from 'rxjs/Observable'
import {NgxMasonryComponent} from 'ngx-masonry'
import {ProjectService} from './project.service'
import {RouterAnimations} from 'app/animations/animations'
import {BrandService} from 'app/brand/brand.service'

declare var SpatialNavigation: any

const PROJECTCOLLECTION = 'projects'
const CREATIVECOLLECTION = 'creatives'
const VERSIONSSUBCOLLECTION = 'versions'
const STATUS = {
  INTERNAL_REVIEW_READY: 'Needs internal review',
  CLIENT_REVIEW_READY: 'Ready for client review',
  REVISION_REQUESTED: 'Revisions requested',
  APPROVED: 'Approved',
  KILLED: 'Killed',
}

@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss'],
  providers: [ProjectService, BrandService],
  animations: [RouterAnimations.routeSlide],
})
export class ProjectComponent implements OnInit {
  archived: any[] = []
  showArchived: boolean = false
  access: String
  projectId: String
  creatives: any[] = []
  visibleItems: any[] = []
  display: string = localStorage.getItem('display') || 'grid'
  project: any
  filters: any
  statuses: any
  next$: Observable<number>
  prev$: Observable<number>
  routeTrigger$: Observable<object>
  STATUS = STATUS
  colors = {
    INTERNAL_REVIEW_READY: '#5FADD5',
    CLIENT_REVIEW_READY: '#FBE470',
    REVISION_REQUESTED: '#E6515B',
    APPROVED: '#6DD982',
    KILLED: '#0003',
  }

  @ViewChild(NgxMasonryComponent) masonry: NgxMasonryComponent

  constructor(
    private firestore: AngularFirestore,
    private auth: AngularFireAuth,
    public route: ActivatedRoute,
    public router: Router,
    private projectService: ProjectService,
    private cdref: ChangeDetectorRef,
    private brandService: BrandService,
    private serializer: UrlSerializer
  ) {
    this.setupRouting()
  }

  ngOnInit() {
    this.route.params.subscribe(({projectId, creativeId, filters}) => {
      if (!projectId) return
      this.projectId = projectId
      try {
        this.getProject()
        this.getCreatives()
      } catch (error) {
        console.log(error)
      }
    })
  }

  getProject() {
    this.access = null
    this.firestore
      .doc([PROJECTCOLLECTION, this.projectId].join('/'))
      .valueChanges()
      .subscribe(
        project => {
          this.project = project
        },
        error => {
          if (error.code == 'permission-denied') this.access = error.code
          else console.error(error)
        }
      )
  }

  getCreatives() {
    this.firestore
      .collection(CREATIVECOLLECTION, ref => ref.where('projectId', '==', this.projectId).orderBy('date', 'desc'))
      .valueChanges()
      .subscribe(creatives => {
        this.archived = []
        this.creatives = []
        for (let i in creatives) {
          const creative = creatives[i]
          switch (creative['visibility']) {
            case 'public':
              this.creatives.push(creative)
              break
            case 'internal':
              if (this.isBannerboy()) {
                this.creatives.push(creative)
              } else this.getClientVersion(creative['id'])
              break
            case 'archived':
              this.archived.push(creative)
              break
          }
        }
        this.filters = this.getFilters()
        this.statuses = this.creatives.map(creative => creative.status || 'INTERNAL_REVIEW_READY').filter((v, i, a) => a.indexOf(v) === i)
        this.refresh()
      })
  }

  getClientVersion(creativeId) {
    this.firestore
      .collection([CREATIVECOLLECTION, creativeId, VERSIONSSUBCOLLECTION].join('/'), ref => ref.where('visibility', '==', 'public').orderBy('date', 'desc').limit(1))
      .valueChanges()
      .subscribe(versions => {
        if (versions[0]) this.creatives.push(versions[0])
        this.refresh()
      })
  }

  getFilters() {
    let filters = this.creatives.map(creative => this.splitName(creative)).sort((a, b) => b.length - a.length)
    if (filters.length == 0) return {}
    filters = filters[0].map((value, key) =>
      filters
        .map(f => f[key])
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort((a, b) => a.localeCompare(b))
    )
    filters = filters.reduce((object, item, index) => {
      object[index] = item.reduce((object, item) => {
        let p: any = decodeURIComponent(this.route.snapshot.queryParams['filter-' + index])
        if (p.indexOf('[') > -1) p = JSON.parse(p)
        p = Array.isArray(p) ? p : [p]
        object[item] = p.includes(item)
        return object
      }, {})
      return object
    }, {})

    console.log(filters)
    return filters
  }

  matchesFilters(creative) {
    let split = this.splitName(creative)
    return Object.entries(this.filters).every(
      ([groupName, group]) =>
        Object.values(group).every(part => !part) ||
        Object.entries(group).some(([part, active]) => {
          return active && split[groupName] == part
        })
    )
  }

  clearFilters() {
    for (let group in this.filters) {
      for (let filter in this.filters[group]) this.filters[group][filter] = false
    }
    this.refresh()
  }

  splitName(creative) {
    return creative.name.split('_')
  }

  initSpatialNavigation() {
    // Initialize
    SpatialNavigation.init()
    SpatialNavigation.add({
      selector: 'app-creative-preview',
    })
    SpatialNavigation.makeFocusable()
    // SpatialNavigation.focus()
  }

  select(event) {
    SpatialNavigation.focus(event.currentTarget.querySelector('a'))
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges()
  }

  getState(outlet: RouterOutlet) {
    return {
      value: outlet.isActivated ? outlet.activatedRoute.snapshot.params.index : 1,
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const KEY_CODE = {
      '32': 'spacebar',
    }
    this.projectService.metaKey = event.metaKey
    if (this.projectService.currentCreative == -1 && this.projectService.focused.length > 0 && KEY_CODE[event.keyCode] && KEY_CODE[event.keyCode] == 'spacebar') {
      event.preventDefault()
      event.stopPropagation()
      this.preview()
      // this.projectService.focused = []
    }
    if (event.keyCode == 65 && event.metaKey) {
      // CMD + A
      this.selectAll()
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    this.projectService.metaKey = event.metaKey
  }

  setupRouting() {
    this.prev$ = this.projectService.currentCreative$.pipe(
      map(index => (index === 0 ? index : index - 1)),
      share()
    )
    this.next$ = this.projectService.currentCreative$.pipe(
      map(index => (index === this.projectService.creatives.length - 1 ? index : index + 1)),
      share()
    )

    this.routeTrigger$ = this.projectService.currentCreative$.pipe(
      startWith(0),
      pairwise(),
      map(([prev, curr]) => {
        let dir
        if (prev == -1) dir = 'top'
        else if (curr == -1) dir = 'bottom'
        else if (prev - curr == -1 || prev - curr > 1) dir = 'left'
        else dir = 'right'
        const enter = {
          left: 'translateX(100%)',
          right: 'translateX(-100%)',
          top: 'translateY(100%)',
          bottom: 'translateY(-100%)',
        }
        const leave = {
          left: 'translateX(-100%)',
          right: 'translateX(100%)',
          top: 'translateY(-100%)',
          bottom: 'translateY(100%)',
        }
        return {
          value: curr,
          params: {
            enter: enter[dir],
            leave: leave[dir],
          },
        }
      })
    )
  }

  @HostListener('click', ['$event'])
  onClick(event) {
    if (event.target.contains(document.getElementById('creatives'))) this.projectService.focused = []
  }

  closeCarousel() {
    SpatialNavigation.resume()
    this.projectService.currentCreative$.next(-1)
  }

  preview() {
    this.router.navigate(['creative', this.projectService.focused[0]], {
      relativeTo: this.route,
    })
  }

  refresh() {
    if (this.isBannerboy() && this.showArchived) {
      this.projectService.creatives = this.archived.filter(creative => this.matchesFilters(creative))
    } else {
      this.projectService.creatives = this.creatives.filter(creative => this.matchesFilters(creative))
    }
    this.setFiltersParameters()
    this.brandService.setBranding(this.project['branding'])
    // const tree = this.router.createUrlTree([], {queryParams: this.filters})
    // console.log(this.serializer.serialize(tree)) // "/?foo=a&bar=42"
    setTimeout(() => {
      this.initSpatialNavigation()
      if (this.masonry && this.masonry['pendingItems'].length == this.projectService.creatives.length) {
        this.masonry.reloadItems()
        this.masonry['pendingItems'].forEach(item => this.masonry.addPendingItem(item))
        this.masonry.layout()
      }
    }, 50)
  }

  setFiltersParameters() {
    let params = {}
    let f = Object.entries(this.filters)
      .map(([name, values]) =>
        Object.entries(values)
          .filter(([value, activated]) => activated)
          .map(([value, activated]) => value)
      )
      .forEach((filters, index) => {
        if (filters.length > 0) params['filter-' + index] = encodeURIComponent(filters.length == 1 ? filters[0] : JSON.stringify(filters))
      })
    // console.log(params)

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      // queryParamsHandling: 'merge',
    })
  }

  getFiltersParameters() {}

  selectAll() {
    this.projectService.focused = this.creatives.map(c => c.id)
    if (this.showArchived) this.projectService.focused = this.projectService.focused.concat(this.archived.map(c => c.id))
    setTimeout(() => {
      if (window.getSelection) {
        if (window.getSelection().empty) {
          window.getSelection().empty()
        } else if (window.getSelection().removeAllRanges) {
          window.getSelection().removeAllRanges()
        }
      }
    }, 10)
  }

  saveSettings() {
    SpatialNavigation.uninit()
    this.initSpatialNavigation()
    localStorage.setItem('display', this.display)
  }

  archive() {
    var c = confirm('Do you want to archive these creatives?')
    if (!c) return
    this.changeVisibility('archived')
    this.projectService.focused = []
  }

  changeVisibility(value) {
    this.projectService.focused.forEach(creativeId => {
      this.firestore.doc([CREATIVECOLLECTION, creativeId].join('/')).update({
        visibility: value,
      })
    })
  }

  canToggleVisibility(visibility) {
    return this.projectService.focused.some(item => this.getCreative(item).visibility != visibility)
  }

  getCreative(creativeId) {
    return this.creatives.concat(this.archived).find(c => c.id == creativeId)
  }

  isBannerboy() {
    return this.auth.auth.currentUser && this.auth.auth.currentUser.email.includes('@bannerboy.com')
  }
}



// WEBPACK FOOTER //
// /Users/axel/Sites/Bannerboy/Internal/attaboy-angular/src/app/project/project.component.ts